import {
  IOfferingSummaryResSchema,
  OFFERING_TYPE_DISPLAY,
  OfferingStatusEnum,
  OfferingTypesEnum,
} from '@monetize/types/app';
import { createColumnHelper } from '../../../components/Monetize/DataTableNew/appTable';
import { actionCatalogColumn } from '../../../components/Monetize/DataTableNew/columnCatalog/structuralColumns';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  dateBodyTemplate,
  dateTimeBodyTemplate,
  enumDisplayBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  getColumnFilterValuesForEnum,
  nameWithIdBodyTemplate,
} from '../../../components/Monetize/DataTableNew/tableUtilsNew';
import { OFFERING_STATUS_DISPLAY } from '../../../constants/offerings';
import { getOfferingEditRoute } from '../../../constants/routes';

const columnHelper = createColumnHelper<IOfferingSummaryResSchema>();

// Only display the 4 active-facing statuses; DRAFT and others are excluded intentionally
const offeringStatusFilterValues = [
  { label: OFFERING_STATUS_DISPLAY.ACTIVE, value: OfferingStatusEnum.ACTIVE },
  {
    label: OFFERING_STATUS_DISPLAY.INACTIVE,
    value: OfferingStatusEnum.INACTIVE,
  },
  {
    label: OFFERING_STATUS_DISPLAY.CANCELED,
    value: OfferingStatusEnum.CANCELED,
  },
  {
    label: OFFERING_STATUS_DISPLAY.ARCHIVED,
    value: OfferingStatusEnum.ARCHIVED,
  },
];

export type OfferingListColumnContext =
  ColumnContextBase<IOfferingSummaryResSchema>;

/** tableId: FilterStateKeys.OFFERING_LIST */
export const OFFERING_LIST_CATALOG: ColumnCatalog<
  IOfferingSummaryResSchema,
  OfferingListColumnContext
> = [
  {
    id: 'name',
    label: 'Name',
    customizable: false,
    build: () =>
      columnHelper.accessor('name', {
        ...getColHeaderWithSize('Name', 400),
        cell: nameWithIdBodyTemplate<IOfferingSummaryResSchema, unknown>({
          idProp: 'id',
          nameProp: 'name',
          idLinkFn: (id) => getOfferingEditRoute(id),
        }),
        enableSorting: true,
        enableResizing: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'type',
    label: 'Offering Type',
    build: () =>
      columnHelper.accessor('type', {
        ...getColHeaderWithSize('Offering Type', 200),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            OfferingTypesEnum,
            OFFERING_TYPE_DISPLAY,
          ),
        }),
        cell: enumDisplayBodyTemplate({
          displayMap: OFFERING_TYPE_DISPLAY,
        }),
        enableSorting: true,
        enableResizing: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'startDate',
    label: 'Start Date',
    build: () =>
      columnHelper.accessor('startDate', {
        ...getColHeaderWithSize('Start Date', 180),
        ...getColHeaderFilters('DATE'),
        cell: dateBodyTemplate(),
        enableSorting: true,
        enableResizing: true,
      }),
  },
  {
    id: 'endDate',
    label: 'End Date',
    build: () =>
      columnHelper.accessor('endDate', {
        ...getColHeaderWithSize('End Date', 180),
        ...getColHeaderFilters('DATE'),
        cell: dateBodyTemplate(),
        enableSorting: true,
        enableResizing: true,
      }),
  },
  {
    id: 'status',
    label: 'Status',
    build: () =>
      columnHelper.accessor('status', {
        ...getColHeaderWithSize('Status', 150),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: offeringStatusFilterValues,
        }),
        cell: enumDisplayBodyTemplate({
          displayMap: OFFERING_STATUS_DISPLAY,
        }),
        enableSorting: true,
        sortFn: 'text',
      }),
  },
  {
    id: 'modifyDate',
    label: 'Updated',
    build: () =>
      columnHelper.accessor('modifyDate', {
        header: 'Updated',
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        size: 180,
        enableSorting: true,
      }),
  },
  {
    id: 'createDate',
    label: 'Created',
    build: () =>
      columnHelper.accessor('createDate', {
        header: 'Created',
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        size: 180,
        enableSorting: true,
      }),
  },
  actionCatalogColumn<IOfferingSummaryResSchema>(),
];
